import {
  CONTENT_MEDIA_POSITIONS,
  type ContentMediaPosition,
  type HeroMediaKind,
  LANDING_PAGE_BACKGROUND_FILLS,
  LANDING_PAGE_CTA_MODES,
  LANDING_PAGE_IMAGE_FITS,
  type LandingPageBackgroundFill,
  type LandingPageBlockType,
  type LandingPageCtaMode,
  type LandingPageImageFit,
} from '@provision/landing-page-utils';

export type { LandingPageBlockType };

/** A block's background in the form; `none` is stored as no `background` at all. */
export type BlockBackgroundOption = 'none' | LandingPageBackgroundFill;

// Every field carries a value whatever the row's `type`: the form's key set must not change, or
// the whole-form dirty check reads a fresh row as dirty. Unowned fields are dropped on serialize.
export interface LandingPageBlockValues {
  id: string;
  type: LandingPageBlockType;

  heroHeadline: string;
  heroSubtext: string;
  heroMediaKind: HeroMediaKind;
  heroVideoUrl: string;
  heroVideoTitle: string;
  heroVideoCaption: string;
  heroImageUrl: string;
  heroImageFit: LandingPageImageFit;
  heroImageFocusX: number;
  heroImageFocusY: number;

  contentKicker: string;
  contentTitle: string;
  contentBody: string;
  contentImageUrl: string;
  contentMediaPosition: ContentMediaPosition;
  contentImageFit: LandingPageImageFit;
  contentImageFocusX: number;
  contentImageFocusY: number;

  plansKicker: string;
  plansTitle: string;
  plansSubtext: string;

  ctaHeadline: string;
  ctaSubtext: string;
  ctaButtonLabel: string;

  backgroundFill: BlockBackgroundOption;
  backgroundColor: string;
  backgroundImageUrl: string;
  backgroundImageFit: LandingPageImageFit;
  backgroundImageFocusX: number;
  backgroundImageFocusY: number;
  /** 0 is auto, as tall as the block's content. */
  minHeight: number;
}

export interface LandingPageSettingsValues {
  ctaMode: LandingPageCtaMode;
  consultationUrl: string;
  showPricing: boolean;
  /** Empty falls back to the client segment's logo. */
  logoUrl: string;
  logoHeight: number;
  headerBackgroundColor: string;
  headerTextColor: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

export interface LandingPageFormValues {
  name: string;
  slug: string;
  blocks: LandingPageBlockValues[];
  settings: LandingPageSettingsValues;
}

const CTA_MODE_LABELS: Record<LandingPageCtaMode, string> = {
  consultation: 'Consultation',
  signup: 'Sign up',
};

const MEDIA_POSITION_LABELS: Record<ContentMediaPosition, string> = {
  auto: 'Auto',
  left: 'Left',
  right: 'Right',
};

const BACKGROUND_LABELS: Record<BlockBackgroundOption, string> = {
  none: 'None',
  solid: 'Solid',
  autoGradient: 'Auto gradient',
  image: 'Image',
};

const IMAGE_FIT_LABELS: Record<LandingPageImageFit, string> = {
  contain: 'Fit',
  cover: 'Fill',
  fill: 'Stretch',
};

export const CTA_MODE_OPTIONS = LANDING_PAGE_CTA_MODES.map((value) => ({
  value,
  label: CTA_MODE_LABELS[value],
}));

export const MEDIA_POSITION_OPTIONS = CONTENT_MEDIA_POSITIONS.map((value) => ({
  value,
  label: MEDIA_POSITION_LABELS[value],
}));

// Shaped for `SelectMenu`, which titles its options rather than labelling them.
export const BACKGROUND_OPTIONS = (['none', ...LANDING_PAGE_BACKGROUND_FILLS] as const).map(
  (value) => ({ value, title: BACKGROUND_LABELS[value] }),
);

export const IMAGE_FIT_OPTIONS = LANDING_PAGE_IMAGE_FITS.map((value) => ({
  value,
  label: IMAGE_FIT_LABELS[value],
}));

export const BLOCK_TYPE_LABELS: Record<LandingPageBlockType, string> = {
  hero: 'Hero',
  content: 'Content section',
  plans: 'Membership plans',
  faq: 'FAQ',
  closingCta: 'Call to Action',
};

export const BLOCK_TYPE_ICONS: Record<LandingPageBlockType, string> = {
  hero: 'image',
  content: 'view_agenda',
  plans: 'list_alt',
  faq: 'help',
  closingCta: 'campaign',
};

export const BLOCK_SYNCED_NOTES: Partial<Record<LandingPageBlockType, string>> = {
  plans:
    'Plans, pricing, features, and tooltips render from your membership configuration and stay in sync automatically. Edit in Membership Options. Leave a heading field blank to use the default.',
  faq: 'Questions and answers sync from your activation flow configuration.',
};

export const BLOCK_TYPE_ORDER = Object.keys(BLOCK_TYPE_LABELS) as LandingPageBlockType[];

type LandingPageBlockField = Exclude<keyof LandingPageBlockValues, 'id' | 'type'>;

export function blockFieldName<F extends LandingPageBlockField>(
  index: number,
  field: F,
): `blocks.${number}.${F}` {
  return `blocks.${index}.${field}`;
}
